import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@honestica/curecall-ui'
import type { THumanReviewListItem } from './human-review-requests.type'
import HumanReviewPatientDropdown from './human-review-patient-dropdown.component'

type TDateGroup = [string, THumanReviewListItem[]]

const groupByDate = (items: THumanReviewListItem[], locale: string): TDateGroup[] => {
  const sorted = [...items].sort(
    (itemA, itemB) =>
      new Date(itemB.patientMedEventDate).getTime() - new Date(itemA.patientMedEventDate).getTime(),
  )

  return sorted.reduce((groups, listItem) => {
    const label = new Date(listItem.patientMedEventDate).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    const existingIndex = groups.findIndex(([groupLabel]) => groupLabel === label)
    if (existingIndex < 0) {
      groups.push([label, [listItem]])
    } else {
      groups[existingIndex]?.[1].push(listItem)
    }
    return groups
  }, [] as TDateGroup[])
}

type Props = {
  items: THumanReviewListItem[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  selectedPatientMedEventId: number | null
  onSelect: (patientMedEventId: number) => void
  onLoadMore: () => void
}

const HumanReviewPatientList = ({
  items,
  hasNextPage,
  isFetchingNextPage,
  selectedPatientMedEventId,
  onSelect,
  onLoadMore,
}: Props) => {
  const { t, i18n } = useTranslation()
  const groups = groupByDate(items, i18n.language)

  return (
    <div className="c-w-40 lg:c-w-64 2xl:c-w-80 c-overflow-scroll c-flex c-flex-col c-shrink-0 c-min-h-full c-pb-12 c-border-r c-border-grey-300">
      {groups.map(([label, groupItems]) => (
        <HumanReviewPatientDropdown
          key={label}
          label={label}
          items={groupItems}
          selectedPatientMedEventId={selectedPatientMedEventId}
          onSelect={onSelect}
        />
      ))}
      {hasNextPage && (
        <div className="c-flex c-justify-center c-p-4">
          <button
            type="button"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
            className="c-inline-flex c-items-center c-gap-0.5 c-text-sm c-text-primary c-cursor-pointer c-border-none c-bg-transparent c-p-0 hover:c-underline hover:c-text-primary-900 disabled:c-opacity-50 disabled:c-cursor-default"
          >
            <PlusIcon className="c-h-3 c-w-3 c-mr-1" />
            {t('pages.dashboard.human-review-requests.patient-list.show-more')}
          </button>
        </div>
      )}
    </div>
  )
}

export default HumanReviewPatientList
